import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Check, X, Send } from "lucide-react";
import api from "../lib/api";
import MobileShell, { LocationSelect, useMobileLocation, Card, Empty, Skeleton } from "./MobileShell";

const dayKey = (v) => { const d = new Date(v); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const outcomeOf = (l) => l.tour_context?.tour_outcome || l.tour_outcome;

function TourRow({ l, onSet }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [local, setLocal] = useState(null);
  const done = local || outcomeOf(l);
  const set = async (o) => {
    setBusy(o);
    try { await api.post(`/crm/hubspot/leads/${l.id}/tour-outcome`, { outcome: o }); setLocal(o); toast.success(o === "Tour Completed" ? "Marked as showed" : "Marked as no-show"); onSet(); }
    catch (e) { toast.error("Couldn't save", { description: e.userMessage }); }
    finally { setBusy(null); }
  };
  const name = `${l.first_name || ""} ${l.last_name || ""}`.trim() || l.company || "Lead";
  return (
    <Card className="p-4 space-y-3" data-testid={`m-tour-${l.id}`}>
      <div className="flex items-start gap-3">
        <span className="flex-1 min-w-0">
          <span className="block text-base font-medium truncate">{name}</span>
          <span className="block text-xs text-brand-tertiary truncate">{[l.company, l.location, l.tour_date && new Date(l.tour_date).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })].filter(Boolean).join(" · ")}</span>
        </span>
        {l.phone && <a href={`tel:${l.phone}`} className="p-2.5 border border-brand-border rounded-full active:bg-brand-hover" aria-label="Call"><Phone className="h-4 w-4" /></a>}
      </div>
      {done ? (
        <div className="flex items-center gap-3">
          <p className="flex-1 text-sm text-brand-muted" data-testid={`m-tour-done-${l.id}`}>{done === "Tour Completed" ? "Showed" : done.replace("Tour ", "")} ✓</p>
          {done === "Tour Completed" && (
            <button onClick={() => nav("/m/availability", { state: { lead: l } })} data-testid={`m-tour-proposal-${l.id}`}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl bg-brand-primary text-brand-page text-sm font-medium active:scale-95 transition-transform">
              <Send className="h-4 w-4" /> Send proposal
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => set("Tour Completed")} disabled={!!busy} data-testid={`m-tour-showed-${l.id}`}
            className="inline-flex items-center justify-center gap-2 min-h-[48px] rounded-xl bg-brand-primary text-brand-page font-medium active:scale-95 transition-transform disabled:opacity-50"><Check className="h-4 w-4" /> Showed</button>
          <button onClick={() => set("Tour No-Show")} disabled={!!busy} data-testid={`m-tour-noshow-${l.id}`}
            className="inline-flex items-center justify-center gap-2 min-h-[48px] rounded-xl border border-brand-border font-medium active:scale-95 transition-transform disabled:opacity-50"><X className="h-4 w-4" /> No-show</button>
        </div>
      )}
    </Card>
  );
}

export default function MTours() {
  const { location } = useMobileLocation();
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    api.get("/crm/leads", { params: { stage: "Tour Booked", location: location || "all" } })
      .then(({ data }) => { setLeads(data); setError(null); }).catch((e) => setError(e.userMessage));
  }, [location]);
  useEffect(() => { load(); }, [load]);
  const groups = useMemo(() => {
    const today = dayKey(new Date());
    const g = { today: [], earlier: [], upcoming: [] };
    (leads || []).filter((l) => l.tour_date).forEach((l) => {
      const k = dayKey(l.tour_date);
      if (k === today) g.today.push(l);
      else if (k < today && !outcomeOf(l) && (Date.now() - new Date(l.tour_date)) < 14 * 864e5) g.earlier.push(l);
      else if (k > today && (new Date(l.tour_date) - Date.now()) < 7 * 864e5) g.upcoming.push(l);
    });
    Object.values(g).forEach((a) => a.sort((a, b) => new Date(a.tour_date) - new Date(b.tour_date)));
    return g;
  }, [leads]);
  const Section = ({ title, items, id }) => items.length > 0 && (
    <div className="space-y-2" data-testid={`m-tours-${id}`}>
      <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary px-1">{title} ({items.length})</p>
      {items.map((l) => <TourRow key={l.id} l={l} onSet={load} />)}
    </div>
  );
  return (
    <MobileShell title="Tours">
      <LocationSelect />
      {error && <p className="text-sm text-brand-neg" data-testid="m-tours-error">Tours couldn't load: {error}</p>}
      {!leads && !error ? <Skeleton /> : (
        <>
          <Section title="Today" items={groups.today} id="today" />
          <Section title="Earlier — not marked yet" items={groups.earlier} id="earlier" />
          <Section title="Next 7 days" items={groups.upcoming} id="upcoming" />
          {leads && !groups.today.length && !groups.earlier.length && !groups.upcoming.length && <Empty data-testid="m-tours-empty">No tours booked today or this week.</Empty>}
        </>
      )}
    </MobileShell>
  );
}
